"use client";

import { useState, useRef } from "react";
import { motion } from "framer-motion";
import { BEFORE_AFTER } from "@/lib/marketing/copy";
import { beforeAfterImages } from "@/lib/marketing/images";
import { SectionHeader } from "@/components/common/SectionHeader";

export function BeforeAfterSlider() {
  const [sliderPosition, setSliderPosition] = useState(50);
  const containerRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);

  const handleMove = (clientX: number) => {
    if (!containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setSliderPosition(percentage);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging.current) return;
    handleMove(e.clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging.current) return;
    handleMove(e.touches[0].clientX);
  };

  const startDragging = () => {
    isDragging.current = true;
  };

  const stopDragging = () => {
    isDragging.current = false;
  };

  return (
    <section className="zaklyn-section">
      <div className="zaklyn-container">
        <SectionHeader
          title={BEFORE_AFTER.title}
          subtitle={BEFORE_AFTER.subtitle}
        />

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="max-w-4xl mx-auto"
        >
          <div
            ref={containerRef}
            className="relative aspect-[4/3] rounded-2xl overflow-hidden cursor-ew-resize select-none"
            onMouseMove={handleMouseMove}
            onMouseUp={stopDragging}
            onMouseLeave={stopDragging}
            onTouchMove={handleTouchMove}
            onTouchEnd={stopDragging}
          >
            <div className="absolute inset-0">
              <img
                src={beforeAfterImages.after.src}
                alt={beforeAfterImages.after.alt}
                className="w-full h-full object-cover"
                draggable={false}
              />
            </div>

            <div
              className="absolute inset-0 overflow-hidden"
              style={{ width: `${sliderPosition}%` }}
            >
              <div className="absolute inset-0" style={{ width: `${100 / (sliderPosition / 100)}%` }}>
                <img
                  src={beforeAfterImages.before.src}
                  alt={beforeAfterImages.before.alt}
                  className="w-full h-full object-cover"
                  draggable={false}
                />
              </div>
            </div>

            <div
              className="absolute top-0 bottom-0 w-1 bg-white shadow-lg cursor-ew-resize"
              style={{ left: `${sliderPosition}%`, transform: "translateX(-50%)" }}
              onMouseDown={startDragging}
              onTouchStart={startDragging}
            >
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 bg-white rounded-full shadow-zaklyn-lg flex items-center justify-center">
                <div className="flex items-center gap-0.5">
                  <div className="w-0.5 h-4 bg-primary rounded-full" />
                  <div className="w-0.5 h-4 bg-primary rounded-full" />
                </div>
              </div>
            </div>

            <div className="absolute bottom-4 left-4 px-3 py-1.5 bg-white/90 backdrop-blur-sm rounded-full text-sm font-medium text-foreground">
              {BEFORE_AFTER.beforeLabel}
            </div>
            <div className="absolute bottom-4 right-4 px-3 py-1.5 bg-white/90 backdrop-blur-sm rounded-full text-sm font-medium text-foreground">
              {BEFORE_AFTER.afterLabel}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
